import React, { useCallback, useEffect, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import Sidebar from './Sidebar';
import TopBar from './TopBar';
import { useOverlays } from '../overlays/OverlayProvider';
import { logoutAdmin } from '../../actions/authAction';
import { getAttentionCounts } from '../../actions/dashboardAction';
import { clearSessionStorage, clearLocalStorage } from '../../helpers/helper';
import './shell.css';

const COLLAPSE_BREAKPOINT = 1280;
const COUNTS_REFRESH_MS = 120000;

/**
 * Application shell. Owns the ⌘K binding, the sidebar's attention badge counts,
 * and the sub-1280px collapse to a 64px icon rail.
 */
export default function AppLayout() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { openPalette, confirm } = useOverlays();
  const [counts, setCounts] = useState({});
  const [collapsed, setCollapsed] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < COLLAPSE_BREAKPOINT
  );

  useEffect(() => {
    const onResize = () => setCollapsed(window.innerWidth < COLLAPSE_BREAKPOINT);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Global palette shortcut. Registered here so it works from any screen.
  useEffect(() => {
    const onKeyDown = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        openPalette();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [openPalette]);

  const loadCounts = useCallback(async () => {
    try {
      setCounts((await getAttentionCounts()) || {});
    } catch {
      // Badges are decorative; never surface a failure here.
    }
  }, []);

  useEffect(() => {
    loadCounts();
    const id = setInterval(loadCounts, COUNTS_REFRESH_MS);
    return () => clearInterval(id);
  }, [loadCounts]);

  const handleSignOut = useCallback(async () => {
    const ok = await confirm({
      title: 'Sign out?',
      body: 'You will need to sign in again to get back into the admin panel.',
      confirmLabel: 'Sign out',
    });
    if (!ok) return;
    await logoutAdmin(dispatch);
    clearSessionStorage();
    clearLocalStorage();
    navigate('/login');
    navigate(0);
  }, [confirm, dispatch, navigate]);

  return (
    <div className="sv-app">
      <Sidebar counts={counts} onSignOut={handleSignOut} collapsed={collapsed} />
      <div className="sv-main">
        <TopBar />
        <div className="sv-content">
          <Outlet context={{ counts, reloadCounts: loadCounts }} />
        </div>
      </div>
    </div>
  );
}
